import { Fragment, memo, useCallback, useMemo, type CSSProperties, type KeyboardEvent, type MouseEvent } from 'react'
import { Eye, Film, Info, ListVideo, Megaphone, MoreHorizontal, Music2, Radio, TriangleAlert } from 'lucide-react'
import type { ScheduleItem } from '../../types/index.js'
import {
  ITEM_STATUS_COLORS,
  ITEM_TYPE_COLORS,
  ITEM_STATUS_LABELS,
  PROGRAM_TYPE_LABELS,
  formatDuration,
  formatDurationShort,
  formatTime,
  scheduleItemTitle,
} from '../../lib/utils.js'
import { useOnAirState } from '../../hooks/useOnAirState.js'
import { selectModeFromEvent, type GridSelectMode } from './useGridSelection.js'
import { Button } from '../ui/button.js'
import { Spinner } from '../common/Spinner.js'

interface PlaylistViewProps {
  items: ScheduleItem[]
  loading: boolean
  selectedItemId: string | null
  /** `mode` porte les modificateurs du clic (voir `useGridSelection`). */
  onSelectItem: (item: ScheduleItem, mode: GridSelectMode) => void
  /** Appartenance à la sélection multiple, interrogée ligne par ligne. */
  isMultiSelected: (id: string) => boolean
  onItemContextMenu?: (item: ScheduleItem, x: number, y: number) => void
  date: string
}

function daypartLabel(hour: number): string {
  if (hour < 6) return 'Nuit'
  if (hour < 12) return 'Matinée'
  if (hour < 18) return 'Après-midi'
  if (hour < 20) return 'Access soirée'
  if (hour < 21) return 'Prime'
  return 'Seconde partie de soirée'
}

function itemMetadata(item: ScheduleItem): string {
  if (item.itemType === 'LIVE') {
    const source = item.liveSource?.name ?? 'Source directe'
    const signal = item.liveSource?.status === 'ACTIVE' ? 'signal stable' : 'signal à contrôler'
    return [source, signal, item.notes].filter(Boolean).join(' · ')
  }

  if (item.itemType === 'AD_BREAK') {
    const placements = item.adPlacements ?? []
    const missing = placements.filter(placement => !placement.spot.filePath).length
    return [
      `${placements.length} spot${placements.length > 1 ? 's' : ''}`,
      missing ? `${missing} média${missing > 1 ? 's' : ''} absent${missing > 1 ? 's' : ''}` : null,
      placements[0]?.spot.filePath,
    ].filter(Boolean).join(' · ')
  }

  if (item.itemType === 'JINGLE') return `Élément d’antenne · ${formatDurationShort(item.durationSec)}`
  if (item.itemType === 'INFOSERVICE') return [item.mediaAsset?.fileName, formatDurationShort(item.durationSec)].filter(Boolean).join(' · ')
  if (item.itemType === 'INTERLUDE' || item.itemType === 'FILLER') {
    return [item.interludeClip?.clipType, formatDurationShort(item.durationSec)].filter(Boolean).join(' · ')
  }

  return [
    item.mediaAsset?.fileName,
    item.program?.genre,
    item.airingKind === 'RERUN' ? 'rediffusion' : null,
    item.notes,
  ].filter(Boolean).join(' · ')
}

function itemBadge(item: ScheduleItem): string | null {
  if (item.airingKind === 'RERUN') return 'REDIFF'
  if (item.itemType === 'LIVE') return 'DIRECT'
  if (item.itemType === 'PROGRAM' && item.program?.type) return PROGRAM_TYPE_LABELS[item.program.type]
  return null
}

function ItemIcon({ item }: { item: ScheduleItem }) {
  const props = { size: 18, strokeWidth: 1.8, 'aria-hidden': true as const }
  if (item.itemType === 'LIVE') return <Radio {...props} />
  if (item.itemType === 'AD_BREAK') return <Megaphone {...props} />
  if (item.itemType === 'JINGLE') return <Music2 {...props} />
  if (item.itemType === 'INFOSERVICE') return <Info {...props} />
  return <Film {...props} />
}

export const PlaylistView = memo(function PlaylistView({
  items,
  loading,
  selectedItemId,
  onSelectItem,
  isMultiSelected,
  onItemContextMenu,
  date,
}: PlaylistViewProps) {
  const { onAirItem, nextItem, progress, elapsed, remaining } = useOnAirState()
  const sorted = useMemo(
    () => [...items].sort((first, second) => new Date(first.startTime).getTime() - new Date(second.startTime).getTime()),
    [items],
  )
  const groups = useMemo(() => {
    return sorted.reduce<Array<{ hour: number; items: ScheduleItem[] }>>((result, item) => {
      const hour = new Date(item.startTime).getHours()
      const current = result[result.length - 1]
      if (!current || current.hour !== hour) result.push({ hour, items: [item] })
      else current.items.push(item)
      return result
    }, [])
  }, [sorted])

  if (loading) return <div className="tp-empty-state agenda-playlist-state" role="status"><Spinner /> Chargement…</div>

  if (!sorted.length) {
    return (
      <div className="tp-empty-state agenda-playlist-state">
        <ListVideo className="agenda-playlist-state__icon" size={32} aria-hidden="true" />
        <span>Aucun élément programmé pour ce jour</span>
      </div>
    )
  }

  return (
    <div className="agenda-playlist" role="list" aria-label={`Playlist du ${date}`}>
      {groups.map(group => (
        <Fragment key={group.hour}>
          <div className="agenda-playlist-hour" aria-hidden="true">
            <strong>{group.hour} H</strong>
            <span>{daypartLabel(group.hour)}</span>
          </div>
          {group.items.map(item => (
            <PlaylistRow
              key={item.id}
              item={item}
              selected={item.id === selectedItemId}
              multiSelected={isMultiSelected(item.id)}
              current={item.id === onAirItem?.id}
              next={item.id === nextItem?.id}
              progress={item.id === onAirItem?.id ? progress : 0}
              elapsed={item.id === onAirItem?.id ? elapsed : 0}
              remaining={item.id === onAirItem?.id ? remaining : 0}
              onSelect={onSelectItem}
              onContextMenu={onItemContextMenu}
            />
          ))}
        </Fragment>
      ))}
    </div>
  )
})

const PlaylistRow = memo(function PlaylistRow({
  item,
  selected,
  multiSelected,
  current,
  next,
  progress,
  elapsed,
  remaining,
  onSelect,
  onContextMenu,
}: {
  item: ScheduleItem
  selected: boolean
  multiSelected: boolean
  current: boolean
  next: boolean
  progress: number
  elapsed: number
  remaining: number
  onSelect: (item: ScheduleItem, mode: GridSelectMode) => void
  onContextMenu?: (item: ScheduleItem, x: number, y: number) => void
}) {
  const colors = item.status === 'SCHEDULED' || item.status === 'LOADING'
    ? ITEM_TYPE_COLORS[item.itemType]
    : ITEM_STATUS_COLORS[item.status]
  const title = scheduleItemTitle(item, '—')
  const metadata = itemMetadata(item)
  const badge = itemBadge(item)
  const compact = item.itemType === 'JINGLE'
  const error = item.status === 'ERROR'
  const done = item.status === 'DONE' || item.status === 'SKIPPED'
  const style = {
    '--playlist-item-bg': colors.bg,
    '--playlist-item-border': colors.border,
    '--playlist-item-text': colors.text,
  } as CSSProperties

  const handleClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    // Maj + clic étend la sélection : sans cela le navigateur surlignerait le
    // texte de toutes les lignes traversées.
    if (event.shiftKey) window.getSelection()?.removeAllRanges()
    onSelect(item, selectModeFromEvent(event))
  }, [item, onSelect])
  const handleContextMenu = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    onContextMenu?.(item, event.clientX, event.clientY)
  }, [item, onContextMenu])
  const handleKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!['Enter', ' '].includes(event.key)) return
    event.preventDefault()
    onSelect(item, selectModeFromEvent(event))
  }, [item, onSelect])
  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    onSelect(item, 'REPLACE')
  }, [item, onSelect])
  const handleMenu = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    const bounds = event.currentTarget.getBoundingClientRect()
    onContextMenu?.(item, bounds.right, bounds.bottom)
  }, [item, onContextMenu])

  const currentTime = current
    ? formatTime(new Date(new Date(item.startTime).getTime() + elapsed * 1000).toISOString()).slice(0, 5)
    : null

  const classes = [
    'agenda-playlist-row',
    compact ? 'is-compact' : '',
    item.itemType === 'LIVE' ? 'is-live' : '',
    current ? 'is-current' : '',
    next ? 'is-next' : '',
    selected ? 'is-selected' : '',
    multiSelected ? 'is-multi-selected' : '',
    error ? 'is-error' : '',
    done ? 'is-done' : '',
  ].filter(Boolean).join(' ')

  return (
    <div
      role="listitem"
      className={classes}
      style={style}
      onClick={handleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="agenda-playlist-row__timing">
        <strong>{formatTime(item.startTime).slice(0, 5)}</strong>
        {!compact && <span>{formatDurationShort(item.durationSec)}</span>}
      </div>

      <div className="agenda-playlist-row__content">
        <div className="agenda-playlist-row__rail" aria-hidden="true" />
        <div className="agenda-playlist-row__main">
          <div className="agenda-playlist-row__heading">
            {!current && <ItemIcon item={item} />}
            {current && <span className="agenda-playlist-row__state is-current">ON AIR</span>}
            <strong className="agenda-playlist-row__title">{title}</strong>
            {badge && <span className={`agenda-playlist-row__tag${item.itemType === 'LIVE' ? ' is-live' : ''}`}>{badge}</span>}
            {next && <span className="agenda-playlist-row__state is-next">SUIVANT</span>}
            {compact && <span className="agenda-playlist-row__compact-meta">{metadata}</span>}
          </div>
          {!compact && metadata && <div className="agenda-playlist-row__meta">{metadata}</div>}

          {current && (
            <div className="agenda-playlist-row__progress">
              <progress max={100} value={progress} aria-label={`Progression ${progress} %`} />
              <strong>−{formatDuration(remaining)}</strong>
            </div>
          )}
        </div>

        <div className="agenda-playlist-row__aside">
          {current && item.asRunLog?.durationDiffSec != null ? (
            <span className="agenda-playlist-row__status is-drift">
              DÉRIVE {item.asRunLog.durationDiffSec >= 0 ? '+' : ''}{Math.round(item.asRunLog.durationDiffSec * 1000)} ms
            </span>
          ) : error ? (
            <span className="agenda-playlist-row__status is-error"><TriangleAlert size={15} aria-hidden="true" /> À CORRIGER</span>
          ) : !compact && !current && !next ? (
            <span className={`agenda-playlist-row__status${done ? ' is-done' : ''}`}>
              {item.status === 'DONE' ? 'TERMINÉ' : ITEM_STATUS_LABELS[item.status].toUpperCase()}
            </span>
          ) : null}

          {!compact && !current && !next && (
            <div className="agenda-playlist-row__actions">
              <Button variant="outline" size="icon-lg" title="Voir le détail" aria-label="Voir le détail" onClick={handleOpen}>
                <Eye data-icon="inline-start" aria-hidden="true" />
              </Button>
              {onContextMenu && (
                <Button variant="outline" size="icon-lg" title="Plus d’actions" aria-label="Plus d’actions" onClick={handleMenu}>
                  <MoreHorizontal data-icon="inline-start" aria-hidden="true" />
                </Button>
              )}
            </div>
          )}
        </div>

        {current && currentTime && (
          <div className="agenda-playlist-row__now" aria-label={`${currentTime}, maintenant`}>
            <span>{currentTime} MAINTENANT</span>
          </div>
        )}
      </div>
    </div>
  )
})
